import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import * as React from 'react';
import React__default from 'react';
import { formSubscriptionItems, createForm, version as version$1, fieldSubscriptionItems } from 'final-form';
import '@babel/runtime/helpers/esm/readOnlyError';

var _excluded$3 = ["render", "children", "component"];
// shared logic between components that use either render prop,
// children render function, or component prop
function renderComponent(props, lazyProps, name) {
  var render = props.render,
    children = props.children,
    component = props.component,
    rest = _objectWithoutPropertiesLoose(props, _excluded$3);
  if (component) {
    return /*#__PURE__*/React.createElement(component, Object.assign(lazyProps, rest, {
      children: children,
      render: render
    }));
  }
  if (render) {
    var _result = {};
    Object.defineProperties(_result, Object.getOwnPropertyDescriptors(lazyProps));
    // Only add properties from rest that don't already exist
    var _restDescriptors = Object.getOwnPropertyDescriptors(rest);
    for (var key in _restDescriptors) {
      if (!(key in _result)) {
        Object.defineProperty(_result, key, _restDescriptors[key]);
      }
    }
    if (children !== undefined) {
      _result.children = children;
    }
    return render(_result);
  }
  if (typeof children !== "function") {
    throw new Error("Must specify either a render prop, a render function as children, or a component prop to " + name);
  }
  var result = {};
  Object.defineProperties(result, Object.getOwnPropertyDescriptors(lazyProps));
  // Only add properties from rest that don't already exist
  var restDescriptors = Object.getOwnPropertyDescriptors(rest);
  for (var _key in restDescriptors) {
    if (!(_key in result)) {
      Object.defineProperty(result, _key, restDescriptors[_key]);
    }
  }
  return children(result);
}

function useWhenValueChanges(value, callback, isEqual) {
  if (isEqual === void 0) {
    isEqual = function isEqual(a, b) {
      return a === b;
    };
  }
  var previous = React__default.useRef(value);
  React__default.useEffect(function () {
    if (!isEqual(value, previous.current)) {
      callback();
      previous.current = value;
    }
  });
}

/**
 * A simple hook to create a constant value that lives for
 * the lifetime of the component.
 *
 * Plagiarized from https://github.com/Andarist/use-constant
 *
 * Do NOT reuse this code unless you know what you're doing.
 * Use Andarist's hook; it's more fault tolerant to things like
 * falsy values.
 *
 * @param init - A function to generate the value
 */
function useConstant(init) {
  var ref = React__default.useRef(undefined);
  if (!ref.current) {
    ref.current = init();
  }
  return ref.current;
}

var shallowEqual = function shallowEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== "object" || !a || typeof b !== "object" || !b) {
    return false;
  }
  var keysA = Object.keys(a);
  var keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  var bHasOwnProperty = Object.prototype.hasOwnProperty.bind(b);
  for (var idx = 0; idx < keysA.length; idx++) {
    var key = keysA[idx];
    if (!bHasOwnProperty(key) || a[key] !== b[key]) {
      return false;
    }
  }
  return true;
};

var isSyntheticEvent = function isSyntheticEvent(candidate) {
  return !!(candidate && typeof candidate.stopPropagation === "function");
};

var ReactFinalFormContext = /*#__PURE__*/React.createContext(undefined);

var addLazyState = function addLazyState(dest, state, keys) {
  keys.forEach(function (key) {
    Object.defineProperty(dest, key, {
      get: function get() {
        return state[key];
      },
      enumerable: true
    });
  });
};
var addLazyFormState = function addLazyFormState(dest, state) {
  return addLazyState(dest, state, ["active", "dirty", "dirtyFields", "dirtySinceLastSubmit", "dirtyFieldsSinceLastSubmit", "error", "errors", "hasSubmitErrors", "hasValidationErrors", "initialValues", "invalid", "modified", "modifiedSinceLastSubmit", "pristine", "submitError", "submitErrors", "submitFailed", "submitSucceeded", "submitting", "touched", "valid", "validating", "values", "visited"]);
};
var addLazyFieldMetaState = function addLazyFieldMetaState(dest, state) {
  return addLazyState(dest, state, ["active", "data", "dirty", "dirtySinceLastSubmit", "error", "initial", "invalid", "length", "modified", "modifiedSinceLastSubmit", "pristine", "submitError", "submitFailed", "submitSucceeded", "submitting", "touched", "valid", "validating", "visited"]);
};

var version = "7.0.0";

var _excluded$2 = ["debug", "decorators", "destroyOnUnregister", "form", "initialValues", "initialValuesEqual", "keepDirtyOnReinitialize", "mutators", "onSubmit", "subscription", "validate", "validateOnBlur"];
var versions = {
  "final-form": version$1,
  "react-final-form": version
};
var all$1 = formSubscriptionItems.reduce(function (result, key) {
  result[key] = true;
  return result;
}, {});
function ReactFinalForm(_ref) {
  var debug = _ref.debug,
    _ref$decorators = _ref.decorators,
    decorators = _ref$decorators === void 0 ? [] : _ref$decorators,
    destroyOnUnregister = _ref.destroyOnUnregister,
    alternateFormApi = _ref.form,
    initialValues = _ref.initialValues,
    initialValuesEqual = _ref.initialValuesEqual,
    keepDirtyOnReinitialize = _ref.keepDirtyOnReinitialize,
    mutators = _ref.mutators,
    onSubmit = _ref.onSubmit,
    _ref$subscription = _ref.subscription,
    subscription = _ref$subscription === void 0 ? all$1 : _ref$subscription,
    validate = _ref.validate,
    validateOnBlur = _ref.validateOnBlur,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var config = {
    debug: debug,
    destroyOnUnregister: destroyOnUnregister,
    initialValues: initialValues,
    keepDirtyOnReinitialize: keepDirtyOnReinitialize,
    mutators: mutators,
    onSubmit: onSubmit,
    validate: validate,
    validateOnBlur: validateOnBlur
  };
  var form = useConstant(function () {
    var f = alternateFormApi || createForm(config);
    // pause validation until children register all fields on first render (unpaused in useEffect() below)
    f.pauseValidation();
    return f;
  });

  // Get initial state without triggering callbacks during render
  var _React$useState = React.useState(function () {
      // Get initial state synchronously but without callbacks
      return form.getState();
    }),
    state = _React$useState[0],
    setState = _React$useState[1];

  // save a copy of state that can break through the closure
  // on the shallowEqual() line below.
  var stateRef = React.useRef(state);
  stateRef.current = state;
  React.useEffect(function () {
    // We have rendered, so all fields are now registered, so we can unpause validation
    form.isValidationPaused() && form.resumeValidation();
    var unsubscriptions = [form.subscribe(function (s) {
      setState(function (prevState) {
        if (!shallowEqual(s, prevState)) {
          return s;
        }
        return prevState;
      });
    }, subscription)].concat(decorators ? decorators.map(function (decorator) {
      return decorator(form);
    }) : []);
    return function () {
      form.pauseValidation(); // pause validation so we don't revalidate on every field deregistration
      unsubscriptions.reverse().forEach(function (unsubscribe) {
        return unsubscribe();
      });
      // don't need to resume validation here; either unmounting, or will re-run this hook with new deps
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // warn about decorator changes
  // istanbul ignore next
  if (process.env.NODE_ENV !== "production") {
    // You're never supposed to use hooks inside a conditional, but in this
    // case we can be certain that you're not going to be changing your
    // NODE_ENV between renders, so this is safe.

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useWhenValueChanges(decorators, function () {
      console.error("Form decorators should not change from one render to the next as new values will be ignored");
    }, shallowEqual);
  }

  // allow updatable config
  useWhenValueChanges(debug, function () {
    form.setConfig("debug", debug);
  });
  useWhenValueChanges(destroyOnUnregister, function () {
    form.destroyOnUnregister = !!destroyOnUnregister;
  });
  useWhenValueChanges(keepDirtyOnReinitialize, function () {
    form.setConfig("keepDirtyOnReinitialize", keepDirtyOnReinitialize);
  });
  useWhenValueChanges(initialValues, function () {
    form.setConfig("initialValues", initialValues);
  }, initialValuesEqual || shallowEqual);
  useWhenValueChanges(mutators, function () {
    form.setConfig("mutators", mutators);
  });
  useWhenValueChanges(onSubmit, function () {
    form.setConfig("onSubmit", onSubmit);
  });
  useWhenValueChanges(validate, function () {
    form.setConfig("validate", validate);
  });
  useWhenValueChanges(validateOnBlur, function () {
    form.setConfig("validateOnBlur", validateOnBlur);
  });
  var handleSubmit = function handleSubmit(event) {
    if (event) {
      // sometimes not true, e.g. React Native
      if (typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (typeof event.stopPropagation === "function") {
        // prevent any outer forms from receiving the event too
        event.stopPropagation();
      }
    }
    return form.submit();
  };
  var renderProps = {
    form: _extends({}, form, {
      reset: function reset(eventOrValues) {
        if (isSyntheticEvent(eventOrValues)) {
          // it's a React SyntheticEvent, call reset with no arguments
          form.reset();
        } else {
          form.reset(eventOrValues);
        }
      }
    }),
    handleSubmit: handleSubmit
  };
  addLazyFormState(renderProps, state);
  return /*#__PURE__*/React.createElement(ReactFinalFormContext.Provider, {
    value: form
  }, renderComponent(_extends({}, rest, {
    __versions: versions
  }), renderProps, "ReactFinalForm"));
}

function useForm(componentName) {
  var form = React.useContext(ReactFinalFormContext);
  if (!form) {
    throw new Error((componentName || "useForm") + " must be used inside of a <Form> component");
  }
  return form;
}

function useFormState(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
    onChange = _ref.onChange,
    _ref$subscription = _ref.subscription,
    subscription = _ref$subscription === void 0 ? all$1 : _ref$subscription;
  var form = useForm("useFormState");
  var onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Initialize state with current form state without callbacks
  var _React$useState = React.useState(function () {
      // Get initial state synchronously but without callbacks
      return form.getState();
    }),
    state = _React$useState[0],
    setState = _React$useState[1];
  React.useEffect(function () {
    // Subscribe to form state changes after initial render
    var unsubscribe = form.subscribe(function (newState) {
      setState(function (prevState) {
        if (!shallowEqual(newState, prevState)) {
          if (onChangeRef.current) {
            onChangeRef.current(newState);
          }
          return newState;
        }
        return prevState;
      });
    }, subscription);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var lazyState = {};
  addLazyFormState(lazyState, state);
  return lazyState;
}

var _excluded$1 = ["onChange", "subscription"];
function FormSpy(_ref) {
  var onChange = _ref.onChange,
    subscription = _ref.subscription,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var reactFinalForm = useForm("FormSpy");
  var state = useFormState({
    onChange: onChange,
    subscription: subscription
  });
  if (onChange) {
    return null;
  }
  var renderProps = _extends({}, state, {
    form: _extends({}, reactFinalForm, {
      reset: function reset(eventOrValues) {
        if (isSyntheticEvent(eventOrValues)) {
          // it's a React SyntheticEvent, call reset with no arguments
          reactFinalForm.reset();
        } else {
          reactFinalForm.reset(eventOrValues);
        }
      }
    })
  });
  return renderComponent(_extends({}, rest, renderProps), state, "FormSpy");
}

var isReactNative = !!(typeof window !== "undefined" && window.navigator && window.navigator.product && window.navigator.product === "ReactNative");

var getSelectedValues = function getSelectedValues(options) {
  var result = [];
  if (options) {
    for (var index = 0; index < options.length; index++) {
      var option = options[index];
      if (option.selected) {
        result.push(option.value);
      }
    }
  }
  return result;
};
var getValue = function getValue(event, currentValue, valueProp, isReactNative) {
  if (!isReactNative && event.nativeEvent && event.nativeEvent.text !== undefined) {
    return event.nativeEvent.text;
  }
  if (isReactNative && event.nativeEvent) {
    return event.nativeEvent.text;
  }
  var detypedEvent = event;
  var _detypedEvent$target = detypedEvent.target,
    type = _detypedEvent$target.type,
    value = _detypedEvent$target.value,
    checked = _detypedEvent$target.checked;
  switch (type) {
    case "checkbox":
      if (valueProp !== undefined) {
        // we are maintaining an array, not just a boolean
        if (checked) {
          // add value to current array value
          return Array.isArray(currentValue) ? currentValue.concat(valueProp) : [valueProp];
        } else {
          // remove value from current array value
          if (!Array.isArray(currentValue)) {
            return currentValue;
          }
          var index = currentValue.indexOf(valueProp);
          if (index < 0) {
            return currentValue;
          } else {
            return currentValue.slice(0, index).concat(currentValue.slice(index + 1));
          }
        }
      } else {
        // it's just a boolean
        return !!checked;
      }
    case "select-multiple":
      return getSelectedValues(event.target.options);
    default:
      return value;
  }
};

function useLatest(value) {
  var ref = React__default.useRef(value);
  React__default.useEffect(function () {
    ref.current = value;
  });
  return ref;
}

/**
 * Creates a callback, even with closures, that will be
 * instance === for the lifetime of the component, always
 * calling the most recent version of the function and its
 * closures.
 */
function useConstantCallback(callback) {
  var ref = React.useRef(callback);
  React.useEffect(function () {
    ref.current = callback;
  });
  return React.useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return ref.current.apply(null, args);
  }, []);
}

var all = fieldSubscriptionItems.reduce(function (result, key) {
  result[key] = true;
  return result;
}, {});
var defaultFormat = function defaultFormat(value, _name) {
  return value === undefined ? "" : value;
};
var defaultParse = function defaultParse(value, _name) {
  return value === "" ? undefined : value;
};
var defaultIsEqual = function defaultIsEqual(a, b) {
  return a === b;
};
function useField(name, config) {
  if (config === void 0) {
    config = {};
  }
  var _config = config,
    afterSubmit = _config.afterSubmit,
    allowNull = _config.allowNull,
    component = _config.component,
    data = _config.data,
    defaultValue = _config.defaultValue,
    _config$format = _config.format,
    format = _config$format === void 0 ? defaultFormat : _config$format,
    formatOnBlur = _config.formatOnBlur,
    initialValue = _config.initialValue,
    multiple = _config.multiple,
    _config$parse = _config.parse,
    parse = _config$parse === void 0 ? defaultParse : _config$parse,
    _config$subscription = _config.subscription,
    subscription = _config$subscription === void 0 ? all : _config$subscription,
    type = _config.type,
    validateFields = _config.validateFields,
    _value = _config.value;
  var form = useForm("useField");
  var configRef = useLatest(config);
  var register = function register(callback, silent) {
    return (
      // avoid using `state` const in any closures created inside `register`
      // because they would refer `state` from current execution context
      // whereas actual `state` would defined in the subsequent `useField` hook
      // execution
      // (that would be caused by `setState` call performed in `register` callback)
      form.registerField(name, callback, subscription, {
        afterSubmit: afterSubmit,
        beforeSubmit: function beforeSubmit() {
          var _configRef$current = configRef.current,
            beforeSubmit = _configRef$current.beforeSubmit,
            formatOnBlur = _configRef$current.formatOnBlur,
            _configRef$current$fo = _configRef$current.format,
            format = _configRef$current$fo === void 0 ? defaultFormat : _configRef$current$fo;
          if (formatOnBlur) {
            var fieldState = form.getFieldState(name);
            if (fieldState) {
              var value = fieldState.value;
              var formatted = format(value, name);
              if (formatted !== value) {
                form.change(name, formatted);
              }
            }
          }
          return beforeSubmit && beforeSubmit();
        },
        data: data,
        defaultValue: defaultValue,
        getValidator: function getValidator() {
          return configRef.current.validate;
        },
        initialValue: initialValue,
        isEqual: function isEqual(a, b) {
          return (configRef.current.isEqual || defaultIsEqual)(a, b);
        },
        silent: silent,
        validateFields: validateFields
      })
    );
  };

  // Initialize state with proper field state from Final Form without callbacks
  var _React$useState = React.useState(function () {
      // Get the current field state from Final Form without registering callbacks
      var existingFieldState = form.getFieldState(name);
      if (existingFieldState) {
        // If allowNull is true and the initial value was null, preserve it
        // (and its formatted version is not null, meaning it was formatted away)
        if (allowNull && existingFieldState.initial === null && existingFieldState.value !== null) {
          return _extends({}, existingFieldState, {
            value: null,
            // Force value back to null
            initial: null // Ensure our local state's 'initial' also reflects this
          });
        }
        return existingFieldState;
      }

      // If no existing state, create a proper initial state
      var initialStateValue = initialValue;
      if (component === "select" && multiple && initialValue === undefined) {
        initialStateValue = [];
      }
      return {
        active: false,
        blur: function blur() {},
        change: function change() {},
        data: data || {},
        dirty: false,
        dirtySinceLastSubmit: false,
        error: undefined,
        focus: function focus() {},
        initial: initialStateValue,
        invalid: false,
        length: undefined,
        modified: false,
        modifiedSinceLastSubmit: false,
        name: name,
        pristine: true,
        submitError: undefined,
        submitFailed: false,
        submitSucceeded: false,
        submitting: false,
        touched: false,
        valid: true,
        validating: false,
        value: initialStateValue,
        visited: false
      };
    }),
    state = _React$useState[0],
    setState = _React$useState[1];
  React.useEffect(function () {
    // Register field after the initial render to avoid setState during render
    var unregister = register(function (newState) {
      setState(function (prevState) {
        // Only update if the state actually changed
        if (!shallowEqual(newState, prevState)) {
          return newState;
        }
        return prevState;
      });
    }, false);
    return unregister;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, data, defaultValue, initialValue]);
  var meta = {};
  addLazyFieldMetaState(meta, state);
  var getInputValue = function getInputValue() {
    var value = state.value;

    // Handle null values first
    if (value === null && !allowNull) {
      value = "";
    }
    if (formatOnBlur) {
      if (component === "input") {
        value = defaultFormat(value);
      }
    } else {
      // Only format if value is not null when allowNull is true
      if (!(allowNull && value === null)) {
        value = format(value, name);
      }
    }
    if (component === "select" && multiple) {
      return Array.isArray(value) ? value : [];
    }
    // For checkboxes and radios, the `value` prop on the input element itself
    // is not the array of selected values or the single selected radio value,
    // but rather the specific value this input represents if selected.
    // The `checked` prop handles the actual selection state.
    // So, for `input.value`, we should return `_value` if provided (for individual inputs in a group)
    // or the formatted field value otherwise (for standalone inputs).
    if ((type === "checkbox" || type === "radio") && _value !== undefined) {
      return _value;
    }
    return value;
  };
  var getInputChecked = function getInputChecked() {
    var value = state.value;
    if (type === "checkbox") {
      value = format(value, name);
      if (_value === undefined) {
        return !!value;
      } else {
        return !!(Array.isArray(value) && ~value.indexOf(_value));
      }
    } else if (type === "radio") {
      return format(value, name) === _value;
    }
    return undefined;
  };
  var input = {
    name: name,
    onBlur: useConstantCallback(function (_event) {
      state.blur();
      if (formatOnBlur) {
        /**
         * Here we must fetch the value directly from Final Form because we cannot
         * trust that our `state` closure has the most recent value. This is a problem
         * if-and-only-if the library consumer has called `onChange()` immediately
         * before calling `onBlur()`, but before the field has had a chance to receive
         * the value update from Final Form.
         */
        var fieldState = form.getFieldState(state.name);
        if (fieldState) {
          state.change(format(fieldState.value, state.name));
        }
      }
    }),
    onChange: useConstantCallback(function (event) {
      // istanbul ignore next
      if (process.env.NODE_ENV !== "production" && event && event.target) {
        var targetType = event.target.type;
        var unknown = ~["checkbox", "radio", "select-multiple"].indexOf(targetType) && !type && component !== "select";
        var _value2 = targetType === "select-multiple" ? state.value : _value;
        if (unknown) {
          console.error("You must pass `type=\"" + (targetType === "select-multiple" ? "select" : targetType) + "\"` prop to your Field(" + name + ") component.\n" + ("Without it we don't know how to unpack your `value` prop - " + (Array.isArray(_value2) ? "[" + _value2 + "]" : "\"" + _value2 + "\"") + "."));
        }
      }
      var value = event && event.target ? getValue(event, state.value, _value, isReactNative) : event;
      state.change(parse(value, name));
    }),
    onFocus: useConstantCallback(function (_event) {
      return state.focus();
    }),
    get value() {
      return getInputValue();
    },
    get checked() {
      return getInputChecked();
    }
  };
  if (multiple) {
    input.multiple = multiple;
  }
  if (type !== undefined) {
    input.type = type;
  }
  var renderProps = {
    input: input,
    meta: meta
  }; // assign to force type check
  return renderProps;
}

var _excluded = ["afterSubmit", "allowNull", "beforeSubmit", "children", "component", "data", "defaultValue", "format", "formatOnBlur", "initialValue", "isEqual", "multiple", "name", "parse", "subscription", "type", "validate", "validateFields", "value"];
function FieldComponent(_ref, ref) {
  var afterSubmit = _ref.afterSubmit,
    allowNull = _ref.allowNull,
    beforeSubmit = _ref.beforeSubmit,
    children = _ref.children,
    component = _ref.component,
    data = _ref.data,
    defaultValue = _ref.defaultValue,
    format = _ref.format,
    formatOnBlur = _ref.formatOnBlur,
    initialValue = _ref.initialValue,
    isEqual = _ref.isEqual,
    multiple = _ref.multiple,
    name = _ref.name,
    parse = _ref.parse,
    subscription = _ref.subscription,
    type = _ref.type,
    validate = _ref.validate,
    validateFields = _ref.validateFields,
    value = _ref.value,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var field = useField(name, {
    afterSubmit: afterSubmit,
    allowNull: allowNull,
    beforeSubmit: beforeSubmit,
    component: component,
    data: data,
    defaultValue: defaultValue,
    format: format,
    formatOnBlur: formatOnBlur,
    initialValue: initialValue,
    isEqual: isEqual,
    multiple: multiple,
    parse: parse,
    subscription: subscription,
    type: type,
    validate: validate,
    validateFields: validateFields,
    value: value
  });
  if (typeof children === "function") {
    return children(_extends({}, field, rest));
  }
  if (typeof component === "string") {
    // ignore meta, combine input with any other props
    var inputProps = _extends({}, field.input);

    // Ensure multiple select has array value
    if (component === "select" && multiple && !Array.isArray(inputProps.value)) {
      inputProps.value = [];
    }
    return /*#__PURE__*/React.createElement(component, _extends({}, inputProps, {
      children: children,
      ref: ref
    }, rest));
  }
  if (!name) {
    throw new Error("prop name cannot be undefined in <Field> component");
  }
  return renderComponent(_extends({
    children: children,
    component: component
  }, rest, field), {}, "Field(" + name + ")");
}

// Create a properly typed forwardRef component that preserves generics
var Field = /*#__PURE__*/React.forwardRef(FieldComponent);

function withTypes() {
  return {
    Form: ReactFinalForm,
    FormSpy: FormSpy
  };
}

export { Field, ReactFinalForm as Form, FormSpy, useField, useForm, useFormState, version, withTypes };
